class HeaderVideoModal {
  constructor() {
    this.modal = document.getElementById('HeaderVideoModal');
    if (!this.modal) return;

    this.media = this.modal.querySelector('deferred-media');
    this.playTimers = [];
    this.bindTriggers();
    this.bindReset();
    this.bindUnmute();
    this.openFromUrl();
    window.addEventListener('hashchange', () => this.openFromUrl());
  }

  isVideoTrigger(element) {
    if (!element) return false;
    if (element.hasAttribute('data-header-video-trigger')) return true;

    const href = element.getAttribute('href') || '';
    if (/#watch-video/i.test(href)) return true;

    const inHeaderMenu = element.closest(
      'nav.header__inline-menu, nav.menu-drawer__navigation, .mega-menu__content, .header__submenu'
    );
    if (!inHeaderMenu) return false;

    return element.textContent.trim().toLowerCase() === 'watch video';
  }

  urlRequestsVideo() {
    const hash = (window.location.hash || '').replace(/^#/, '').toLowerCase();
    if (hash === 'watch-video') return true;

    return new URLSearchParams(window.location.search).has('watch-video');
  }

  openFromUrl() {
    if (!this.urlRequestsVideo()) return;
    if (this.modal.hasAttribute('open')) return;
    this.openModal();
  }

  setShareUrl() {
    const url = new URL(window.location.href);
    if ((url.hash || '').replace(/^#/, '').toLowerCase() === 'watch-video') return;

    url.hash = 'watch-video';
    history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  clearShareUrl() {
    const url = new URL(window.location.href);
    const hashMatch = (url.hash || '').replace(/^#/, '').toLowerCase() === 'watch-video';
    const hadParam = url.searchParams.has('watch-video');
    if (!hashMatch && !hadParam) return;

    if (hadParam) url.searchParams.delete('watch-video');
    history.replaceState(null, '', `${url.pathname}${url.search}`);
  }

  bindTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('a, [data-header-video-trigger]');
      if (!this.isVideoTrigger(trigger)) return;

      event.preventDefault();

      const drawer = trigger.closest('header-drawer');
      if (drawer && typeof drawer.closeMenuDrawer === 'function') {
        drawer.closeMenuDrawer(event, drawer.querySelector('summary'));
      }

      document.querySelectorAll('header-menu details[open]').forEach((details) => {
        details.removeAttribute('open');
      });

      this.openModal(trigger);
    });
  }

  openModal(trigger) {
    if (typeof this.modal.show !== 'function') return;

    const originalPauseAllMedia = window.pauseAllMedia;
    window.pauseAllMedia = () => {
      document.querySelectorAll('.js-youtube').forEach((youtube) => {
        if (this.modal.contains(youtube)) return;
        youtube.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      });
      document.querySelectorAll('.js-vimeo').forEach((vimeo) => {
        if (this.modal.contains(vimeo)) return;
        vimeo.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      document.querySelectorAll('video').forEach((htmlVideo) => {
        if (this.modal.contains(htmlVideo)) return;
        htmlVideo.pause();
      });
      document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      });
    };

    try {
      this.modal.show(trigger || this.modal);
    } finally {
      window.pauseAllMedia = originalPauseAllMedia;
    }

    this.setShareUrl();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.playMedia());
    });
  }

  playMedia() {
    this.clearPlayTimers();

    const video = this.media.querySelector('video');
    if (video) {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.style.display = 'none';
      void video.offsetHeight;
      video.style.display = 'block';
      this.playHtmlVideo(video);
      return;
    }

    const iframe = this.media.querySelector('iframe');
    if (!iframe) return;

    iframe.style.display = 'none';
    void iframe.offsetHeight;
    iframe.style.display = 'block';

    const isYouTube = iframe.classList.contains('js-youtube');
    const sendPlay = () => {
      if (!iframe.contentWindow) return;

      if (isYouTube) {
        iframe.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
          '*'
        );
      } else {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
      }
    };

    this.stopPlayListener();
    this.onPlayerMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (error) {
          return;
        }
      }
      if (!data) return;
      if (data.event === 'onReady' || data.event === 'ready') sendPlay();
    };
    window.addEventListener('message', this.onPlayerMessage);

    iframe.addEventListener(
      'load',
      () => {
        if (isYouTube) {
          iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
        }
        sendPlay();
      },
      { once: true }
    );

    sendPlay();
    [150, 400, 800, 1600].forEach((delay) => {
      this.playTimers.push(setTimeout(sendPlay, delay));
    });
  }

  playHtmlVideo(video) {
    video.muted = false;
    const playAttempt = video.play();
    if (!playAttempt || typeof playAttempt.catch !== 'function') return;

    playAttempt.catch(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
  }

  bindUnmute() {
    this.modal.addEventListener('pointerdown', () => {
      const video = this.media.querySelector('video');
      if (!video || !video.muted) return;
      video.muted = false;
      video.play().catch(() => {});
    });
  }

  stopPlayListener() {
    if (!this.onPlayerMessage) return;
    window.removeEventListener('message', this.onPlayerMessage);
    this.onPlayerMessage = null;
  }

  clearPlayTimers() {
    this.playTimers.forEach((timer) => clearTimeout(timer));
    this.playTimers = [];
  }

  bindReset() {
    if (!this.media) return;

    const observer = new MutationObserver(() => {
      if (this.modal.hasAttribute('open')) return;

      this.clearPlayTimers();
      this.stopPlayListener();
      this.media.removeAttribute('loaded');
      this.media.querySelectorAll('iframe, video').forEach((element) => element.remove());
      this.clearShareUrl();
    });

    observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new HeaderVideoModal();
});
